import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag, Typography, Select, Modal, Space } from 'antd';
import {
  FullscreenOutlined, FullscreenExitOutlined,
  SettingOutlined, WifiOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Text } = Typography;
const STORAGE_KEY          = 'welcome_screen_config';
const DISPLAY_LOCATION_KEY = 'welcome_display_location';
const MAX_CARDS            = 5;

// ─── CSS keyframes injected once ─────────────────────────────────────────────
const STYLE_ID = 'welcome-display-keyframes';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes bgShift {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes slideInCenter {
      from { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
      to   { opacity: 1; transform: translate(-50%, -50%) scale(1);   }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1;   transform: scale(1);    }
      50%       { opacity: 0.55; transform: scale(0.97); }
    }
    @keyframes radarRing {
      0%   { transform: scale(0.6); opacity: 0.9; }
      100% { transform: scale(2.2); opacity: 0;   }
    }
    @keyframes tableIn {
      from { opacity: 0; transform: translateX(40px); }
      to   { opacity: 1; transform: translateX(0);    }
    }
  `;
  document.head.appendChild(style);
}

// ─── Card layout positions (index 0 = latest = center) ───────────────────────
// Each entry: { x offset (px), scale, opacity, zIndex }
const CARD_SLOTS = [
  { dx:    0, scale: 1.00, opacity: 1.00, z: 10 }, // center  (latest)
  { dx:  360, scale: 0.68, opacity: 0.72, z: 8  }, // right-1
  { dx: -360, scale: 0.68, opacity: 0.62, z: 7  }, // left-1
  { dx:  620, scale: 0.50, opacity: 0.42, z: 6  }, // right-2
  { dx: -620, scale: 0.50, opacity: 0.32, z: 5  }, // left-2
];

const CATEGORY_COLORS = {
  VIP: '#f59e0b', Speaker: '#8b5cf6', Exhibitor: '#10b981',
  Attendee: '#3b82f6', Press: '#ec4899', Staff: '#6366f1',
};
const catColor = (c) => CATEGORY_COLORS[c] || '#2563eb';

// ─── Live Clock ───────────────────────────────────────────────────────────────
const CLOCK_POS = {
  'top-left':      { top: 24,   left: 24,                                        textAlign: 'left'   },
  'top-center':    { top: 24,   left: '50%', transform: 'translateX(-50%)',      textAlign: 'center' },
  'top-right':     { top: 24,   right: 90,                                       textAlign: 'right'  },
  'bottom-left':   { bottom: 50, left: 24,                                       textAlign: 'left'   },
  'bottom-center': { bottom: 50, left: '50%', transform: 'translateX(-50%)',     textAlign: 'center' },
  'bottom-right':  { bottom: 50, right: 90,                                      textAlign: 'right'  },
};

function LiveClock({ format24, positionKey }) {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  const pos = CLOCK_POS[positionKey] || CLOCK_POS['top-right'];
  const timeStr = t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: !format24 });
  const dateStr = t.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div style={{ position: 'absolute', zIndex: 12, pointerEvents: 'none', ...pos }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: 2, fontFamily: 'monospace' }}>{timeStr}</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: 1, marginTop: 2 }}>{dateStr}</div>
    </div>
  );
}

// ─── Recent Scans Mini Table ──────────────────────────────────────────────────
function RecentTable({ scans }) {
  if (!scans.length) return null;
  return (
    <div style={{
      position: 'absolute', bottom: 60, right: 18, zIndex: 15,
      width: 280, animation: 'tableIn 0.4s ease forwards',
    }}>
      <div style={{
        background: 'rgba(15,23,42,0.82)', border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 10, overflow: 'hidden',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)',
          fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: 2,
          textTransform: 'uppercase', fontWeight: 600,
        }}>
          Recent Scans
        </div>
        {scans.slice(0, 8).map((s, i) => (
          <div key={s.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 14px',
            borderBottom: i < scans.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
            background: i === 0 ? 'rgba(99,102,241,0.12)' : 'transparent',
            transition: 'background 0.3s',
          }}>
            {i === 0 && (
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#818cf8', flexShrink: 0,
                boxShadow: '0 0 6px #818cf8',
              }} />
            )}
            {i > 0 && <div style={{ width: 6, flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: i === 0 ? 700 : 400,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.name || 'Unknown'}
              </div>
              {s.location && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
                  📍 {s.location}
                </div>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', flexShrink: 0 }}>
              {dayjs(s.scanned_at).format('HH:mm:ss')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Single Delegate Card ─────────────────────────────────────────────────────
function DelegateCard({ scan, config, slotIdx, removing, orderedFields }) {
  const slot = CARD_SLOTS[slotIdx] || CARD_SLOTS[MAX_CARDS - 1];
  const show = (field) => config.fields?.includes(field) !== false;
  const fields = orderedFields || ['photo', 'name', 'category', 'company', 'location', 'welcomeMessage'];
  const isCenter = slotIdx === 0;

  const cardStyle = {
    position: 'absolute',
    top: '50%', left: '50%',
    transform: `translate(calc(-50% + ${slot.dx}px), -50%) scale(${removing ? 0.7 : slot.scale})`,
    opacity: removing ? 0 : slot.opacity,
    zIndex: slot.z,
    transition: 'transform 0.55s cubic-bezier(0.22,1,0.36,1), opacity 0.55s ease',
    textAlign: 'center',
    width: isCenter ? 520 : 380,
    pointerEvents: 'none',
  };

  return (
    <div style={cardStyle}>
      {/* Glass card bg for non-center */}
      {!isCenter && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 16,
          background: 'rgba(15,23,42,0.55)',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(8px)',
        }} />
      )}

      <div style={{ position: 'relative', padding: isCenter ? 0 : '20px 24px' }}>
        {/* Welcome label — only on center */}
        {isCenter && (
          <div style={{
            fontSize: 18, color: 'rgba(255,255,255,0.55)',
            fontWeight: 300, letterSpacing: 6, textTransform: 'uppercase', marginBottom: 10,
          }}>
            Welcome
          </div>
        )}

        {fields.map(field => {
          if (!show(field)) return null;
          switch (field) {
            case 'photo':
              return scan.photo ? (
                <div key="photo" style={{
                  width: isCenter ? 110 : 70, height: isCenter ? 110 : 70,
                  borderRadius: '50%', border: '3px solid rgba(255,255,255,0.25)',
                  overflow: 'hidden', margin: '0 auto 16px',
                }}>
                  <img src={scan.photo} alt={scan.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ) : null;

            case 'name':
              return (
                <div key="name" style={{
                  fontSize: isCenter ? 68 : 28, fontWeight: 800,
                  color: '#ffffff', lineHeight: 1.05, letterSpacing: isCenter ? -1 : 0,
                  textShadow: '0 4px 24px rgba(0,0,0,0.5)', marginBottom: isCenter ? 14 : 6,
                }}>
                  {scan.name || 'Unknown Delegate'}
                </div>
              );

            case 'category':
              return scan.category ? (
                <Tag key="category" style={{
                  fontSize: isCenter ? 14 : 11, padding: isCenter ? '5px 16px' : '2px 10px',
                  borderRadius: 30, background: `${catColor(scan.category)}22`,
                  border: `2px solid ${catColor(scan.category)}`,
                  color: catColor(scan.category), fontWeight: 700, marginBottom: 6,
                }}>
                  {scan.category}
                </Tag>
              ) : null;

            case 'company':
              return scan.company ? (
                <div key="company" style={{
                  fontSize: isCenter ? 20 : 13, color: 'rgba(255,255,255,0.7)',
                  fontWeight: 400, marginBottom: 6,
                }}>
                  {scan.company}
                </div>
              ) : null;

            case 'location':
              return scan.location ? (
                <div key="location" style={{
                  fontSize: isCenter ? 15 : 11, color: 'rgba(255,255,255,0.45)',
                  marginBottom: 6, fontWeight: 300,
                }}>
                  📍 {scan.location}
                </div>
              ) : null;

            case 'welcomeMessage':
              return isCenter ? (
                <div key="msg" style={{
                  marginTop: 16, fontSize: 20, fontWeight: 300,
                  color: 'rgba(255,255,255,0.65)', fontStyle: 'italic',
                }}>
                  {config.welcomeMessage || 'Welcome!'}
                </div>
              ) : null;

            default: return null;
          }
        })}

        {isCenter && (
          <div style={{
            width: 70, height: 2,
            background: 'linear-gradient(90deg, transparent, #818cf8, transparent)',
            margin: '20px auto 0', borderRadius: 2,
          }} />
        )}
      </div>
    </div>
  );
}

// ─── Waiting State ────────────────────────────────────────────────────────────
function WaitingState({ technology }) {
  return (
    <div style={{ textAlign: 'center', userSelect: 'none' }}>
      <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto 40px' }}>
        {[0, 0.5, 1].map((delay, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid rgba(129,140,248,0.5)',
            animation: `radarRing 2.4s ${delay}s ease-out infinite`,
          }} />
        ))}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WifiOutlined style={{ fontSize: 38, color: '#818cf8', animation: 'pulse 2s ease-in-out infinite' }} />
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 300, color: 'rgba(255,255,255,0.8)', letterSpacing: 1, animation: 'pulse 3s ease-in-out infinite' }}>
        {technology === 'nfc' ? 'Tap your NFC wristband to check in' : 'Scan your badge to check in'}
      </div>
      <div style={{ marginTop: 14, fontSize: 16, color: 'rgba(255,255,255,0.3)', letterSpacing: 3, textTransform: 'uppercase' }}>
        Identium RFID Delegate Management
      </div>
    </div>
  );
}

// ─── Corner Controls ──────────────────────────────────────────────────────────
function CornerControls({ isFullscreen, toggleFullscreen, technology, connected, navigate, activeLocation, onChangeLocation }) {
  return (
    <>
      <div style={{ position: 'absolute', top: 18, right: 18, display: 'flex', gap: 6, alignItems: 'center', zIndex: 20 }}>
        <Button type="text" icon={<EnvironmentOutlined />}
          onClick={onChangeLocation}
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, height: 34, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          {activeLocation || 'Set Location'}
        </Button>
        <Button type="text" icon={<SettingOutlined />}
          onClick={() => navigate('/welcome-config')}
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 17, width: 34, height: 34 }}
        />
        <Button type="text"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={toggleFullscreen}
          style={{ color: 'rgba(255,255,255,0.55)', fontSize: 17, width: 34, height: 34 }}
        />
      </div>
      <div style={{ position: 'absolute', bottom: 18, left: 18, display: 'flex', alignItems: 'center', gap: 8, zIndex: 20 }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%',
          background: connected ? '#22c55e' : '#ef4444',
          boxShadow: connected ? '0 0 6px #22c55e' : '0 0 6px #ef4444',
        }} />
        <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
          {technology === 'uhf' ? 'UHF / Active RFID' : 'NFC'} · {connected ? 'Connected' : 'Offline'}
        </Text>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WelcomeDisplay() {
  const navigate = useNavigate();

  const [config, setConfig] = useState({
    technology: 'uhf',
    fields: ['name', 'category', 'company', 'welcomeMessage'],
    fieldOrder: ['photo', 'name', 'category', 'company', 'location', 'welcomeMessage'],
    welcomeMessage: 'Welcome to the Event!',
    displayDuration: 8,
    logoUrl: null, logoPlacement: 'center',
    bgType: 'gradient', bgColor: '#0f172a', bgImageUrl: null,
    showClock: false, clockPosition: 'top-right', clockFormat: '24h',
  });

  // Per-device location (each TV picks its own)
  const [displayCfg, setDisplayCfg]   = useState({ event_id: null, location_id: null });
  const [showSetup, setShowSetup]     = useState(false);
  const [events, setEvents]           = useState([]);
  const [locations, setLocations]     = useState([]);
  const [setupEvent, setSetupEvent]   = useState(null);
  const [setupLoc, setSetupLoc]       = useState(null);

  // Multi-card state
  // Each entry: { key, scan, removing }
  const [activeCards, setActiveCards]   = useState([]);
  // Recent scans table (newest first, up to 8)
  const [recentScans, setRecentScans]   = useState([]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connected, setConnected]       = useState(true);

  const seenIdsRef       = useRef(new Set());
  const recentTagsRef    = useRef(new Map()); // rfid_tag → ms timestamp last shown
  const isInitRef        = useRef(true);
  const pollIntervalRef  = useRef(null);
  const nfcInputRef      = useRef(null);
  const nfcValueRef      = useRef('');
  const fadeTimersRef    = useRef({}); // key → timer id

  // ── Load config ──────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setConfig(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch {}
    try {
      const loc = localStorage.getItem(DISPLAY_LOCATION_KEY);
      if (loc) {
        const p = JSON.parse(loc);
        setDisplayCfg(p);
        setSetupEvent(p.event_id);
        setSetupLoc(p.location_id);
      } else {
        setShowSetup(true);
      }
    } catch {
      setShowSetup(true);
    }
  }, []);

  useEffect(() => {
    axios.get('/api/welcome/events').then(r => setEvents(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!setupEvent) { setLocations([]); setSetupLoc(null); return; }
    axios.get('/api/welcome/locations', { params: { event_id: setupEvent } })
      .then(r => setLocations(r.data.data || [])).catch(() => {});
  }, [setupEvent]);

  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  // ── Add a new card ────────────────────────────────────────────────────────────
  const addCard = useCallback((scan) => {
    const key = `card-${scan.id}`;

    setActiveCards(prev => {
      // Limit to MAX_CARDS, newest first
      const next = [{ key, scan, removing: false }, ...prev].slice(0, MAX_CARDS);
      return next;
    });

    setRecentScans(prev => [scan, ...prev].slice(0, 8));

    // Auto-fade after displayDuration
    const dur = config.displayDuration || 8;
    if (fadeTimersRef.current[key]) clearTimeout(fadeTimersRef.current[key]);
    fadeTimersRef.current[key] = setTimeout(() => {
      // Mark as removing → triggers fade CSS transition
      setActiveCards(prev => prev.map(c => c.key === key ? { ...c, removing: true } : c));
      // Remove from DOM after transition completes
      setTimeout(() => {
        setActiveCards(prev => prev.filter(c => c.key !== key));
        delete fadeTimersRef.current[key];
      }, 600);
    }, dur * 1000);
  }, [config.displayDuration]);

  // ── Poll for new scans ────────────────────────────────────────────────────────
  const pollScans = useCallback(async () => {
    try {
      const params = { limit: MAX_CARDS };
      if (displayCfg.event_id)    params.event_id    = displayCfg.event_id;
      if (displayCfg.location_id) params.location_id = displayCfg.location_id;

      const res = await axios.get('/api/welcome/recent-scans', { params });
      setConnected(true);
      const scans = res.data || [];

      if (isInitRef.current) {
        // Seed on first poll — don't display existing scans, and mark tags as "just seen"
        scans.forEach(s => {
          seenIdsRef.current.add(s.id);
          recentTagsRef.current.set(s.rfid_tag, Date.now());
        });
        isInitRef.current = false;
        return;
      }

      const cooldownMs = (config.displayDuration || 8) * 1000;
      const now = Date.now();

      // Find new scan IDs, oldest first so they enter in chronological order
      const newScans = scans.filter(s => !seenIdsRef.current.has(s.id)).reverse();
      newScans.forEach(s => {
        seenIdsRef.current.add(s.id);
        // Skip if the same tag was shown recently (still within cooldown window)
        const lastShown = recentTagsRef.current.get(s.rfid_tag);
        if (lastShown && now - lastShown < cooldownMs) return;
        recentTagsRef.current.set(s.rfid_tag, now);
        addCard(s);
      });
    } catch {
      setConnected(false);
    }
  }, [displayCfg.event_id, displayCfg.location_id, addCard, config.displayDuration]);

  useEffect(() => {
    if (config.technology !== 'uhf') return;
    isInitRef.current = true;
    seenIdsRef.current = new Set();
    recentTagsRef.current = new Map();
    setActiveCards([]);
    pollScans();
    pollIntervalRef.current = setInterval(pollScans, 2000);
    return () => clearInterval(pollIntervalRef.current);
  }, [config.technology, displayCfg.event_id, displayCfg.location_id, pollScans]);

  // ── NFC input ─────────────────────────────────────────────────────────────────
  const handleNfcInput = useCallback(async (e) => {
    const uid = e.target.value.trim();
    if (!uid || uid === nfcValueRef.current) return;
    nfcValueRef.current = uid;
    e.target.value = '';
    try {
      const res = await axios.post('/api/welcome/nfc-lookup', { uid });
      setConnected(true);
      if (res.data) addCard({ ...res.data, id: Date.now(), scanned_at: new Date().toISOString() });
    } catch { setConnected(false); }
  }, [addCard]);

  useEffect(() => {
    if (config.technology === 'nfc' && nfcInputRef.current) nfcInputRef.current.focus();
  }, [config.technology]);

  const saveDisplayLocation = () => {
    const cfg = { event_id: setupEvent, location_id: setupLoc };
    localStorage.setItem(DISPLAY_LOCATION_KEY, JSON.stringify(cfg));
    setDisplayCfg(cfg);
    setShowSetup(false);
    isInitRef.current = true;
    seenIdsRef.current = new Set();
    recentTagsRef.current = new Map();
    setActiveCards([]);
    setRecentScans([]);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };

  // ── Background style ──────────────────────────────────────────────────────────
  const bgStyle = (() => {
    if (config.bgType === 'image' && config.bgImageUrl)
      return { background: '#0f172a', backgroundImage: `url(${config.bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    if (config.bgType === 'solid')
      return { background: config.bgColor || '#0f172a' };
    return { background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e, #1a1a6e, #0f0c29)', backgroundSize: '400% 400%', animation: 'bgShift 12s ease infinite' };
  })();

  const activeLocation = displayCfg.location_id
    ? locations.find(l => l.id === displayCfg.location_id)?.location_name
    : (events.find(e => e.id === displayCfg.event_id)?.event_name || null);

  const orderedFields = config.fieldOrder || ['photo', 'name', 'category', 'company', 'location', 'welcomeMessage'];

  return (
    <div
      style={{ position: 'fixed', inset: 0, overflow: 'hidden', fontFamily: "'Segoe UI','Inter',sans-serif", ...bgStyle }}
      onClick={() => config.technology === 'nfc' && nfcInputRef.current?.focus()}
    >
      {config.technology === 'nfc' && (
        <input ref={nfcInputRef} onChange={handleNfcInput} autoFocus
          style={{ position: 'absolute', visibility: 'hidden', width: 1, height: 1, left: -9999 }} tabIndex={-1} />
      )}

      {/* Ambient orbs */}
      {config.bgType !== 'image' && (
        <>
          <div style={{ position: 'absolute', top: '10%', left: '15%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '5%', right: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.16) 0%, transparent 70%)', pointerEvents: 'none' }} />
        </>
      )}

      {/* Logo */}
      {config.logoUrl && (
        <div style={{
          position: 'absolute', top: 28, width: '100%', zIndex: 5,
          display: 'flex',
          justifyContent: config.logoPlacement === 'left' ? 'flex-start' : config.logoPlacement === 'right' ? 'flex-end' : 'center',
          padding: '0 40px', boxSizing: 'border-box',
        }}>
          <img src={config.logoUrl} alt="Logo" style={{ maxHeight: 55, maxWidth: 220, objectFit: 'contain', opacity: 0.88 }} />
        </div>
      )}

      {/* Clock */}
      {config.showClock && <LiveClock format24={config.clockFormat !== '12h'} positionKey={config.clockPosition || 'top-right'} />}

      {/* ── Card stage ── */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {activeCards.length === 0
          ? <WaitingState technology={config.technology} />
          : activeCards.map((c, idx) => (
              <DelegateCard
                key={c.key}
                scan={c.scan}
                config={config}
                slotIdx={idx}
                removing={c.removing}
                orderedFields={orderedFields}
              />
            ))
        }
      </div>

      {/* Recent scans table */}
      <RecentTable scans={recentScans} />

      <CornerControls
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        technology={config.technology}
        connected={connected}
        navigate={navigate}
        activeLocation={activeLocation}
        onChangeLocation={() => setShowSetup(true)}
      />

      {/* Location setup modal */}
      <Modal
        open={showSetup}
        title={<Space><EnvironmentOutlined style={{ color: '#2563eb' }} /><span>Select Display Location</span></Space>}
        onOk={saveDisplayLocation}
        onCancel={() => { if (displayCfg.event_id != null || displayCfg.location_id != null) setShowSetup(false); }}
        okText="Start Display"
        okButtonProps={{ disabled: !setupEvent, style: { background: '#2563eb' } }}
        closable={!!(displayCfg.event_id || displayCfg.location_id)}
        maskClosable={!!(displayCfg.event_id || displayCfg.location_id)}
        centered width={420}
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
            This screen only shows scans from the selected location.
            Each TV/laptop can select a different location independently.
          </p>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Event</div>
            <Select placeholder="Select Event" value={setupEvent || undefined}
              onChange={v => { setSetupEvent(v); setSetupLoc(null); }}
              options={events.map(e => ({ label: e.event_name, value: e.id }))}
              style={{ width: '100%' }} size="large" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Location <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(optional — blank = all locations)</span>
            </div>
            <Select placeholder={setupEvent ? 'All Locations' : 'Select event first'} allowClear
              disabled={!setupEvent} value={setupLoc || undefined}
              onChange={v => setSetupLoc(v || null)}
              options={locations.map(l => ({ label: l.location_name, value: l.id }))}
              style={{ width: '100%' }} size="large" />
          </div>
        </div>
      </Modal>
    </div>
  );
}
